/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AlfrescoApiService, GroupService } from '@alfresco/adf-content-services';
import { NodesApi, PeopleApi } from '@alfresco/js-api';
import { AppStore, SetCurrentUrlAction } from '@alfresco/aca-shared/store';
import { from, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { WorkflowSettingsService } from '../workflow/workflow-settings.service';
import { AcaConfigFolderService } from '../shared/aca-config-folder.service';

/**
 * One set of eyes and what it may see.
 *
 * A policy names people and groups; the menus it lists become visible to
 * them — and ONLY to them. The rule is deliberately strict: once any policy
 * exists at all, a menu that no matching policy names is hidden. Menus are
 * denied by default rather than granted by default, so a menu added to the
 * application later never leaks to eyes it was not listed for.
 */
export interface MenuPolicy {
  id: string;
  name: string;
  /** Alfresco person ids (usernames). */
  users: string[];
  /** Alfresco group ids (`GROUP_*`). */
  groups: string[];
  /** Navigation item ids this policy opens (`MenuItemDef.id`). */
  menus: string[];
}

/** A navigation entry the administrator can grant or withhold. */
export interface MenuItemDef {
  /** Short key — the rule evaluator is registered as `app.menu.<key>`. */
  key: string;
  /** The item id inside `app.custom.extensions.json`, or `app.settings.*` for a section. */
  id: string;
  /** The route path the visibility guard checks — navigation items only. */
  route?: string;
  /** `nav` is a sidebar entry; `settings` is a section inside the settings page. */
  kind: 'nav' | 'settings';
  /** i18n key for the checklist label. */
  titleKey: string;
}

/** Who is looking, distilled to what policy matching needs. */
interface Viewer {
  id: string;
  isAdmin: boolean;
  groups: Set<string>;
}

/**
 * Which navigation entries each person may see, as an administrator set it.
 *
 * Policies are kept where branding, the watermark and the workflow flag keep
 * theirs: one small document in the repository —
 * `Data Dictionary/ACA/menu-permissions.json` — which every signed-in user
 * can read (Data Dictionary grants Consumer to GROUP_EVERYONE) and only an
 * administrator can write.
 *
 * Two safeguards keep the strict default-deny rule from locking everyone
 * out: the feature is inert while no policy has been saved at all, and
 * repository administrators — plus the built-in Alfresco administrators
 * group — always see every menu.
 */
@Injectable({ providedIn: 'root' })
export class MenuPermissionsService {
  private readonly api = inject(AlfrescoApiService);
  private readonly configFolder = inject(AcaConfigFolderService);
  private readonly groupService = inject(GroupService);
  private readonly workflowSettings = inject(WorkflowSettingsService);
  private readonly store = inject<Store<AppStore>>(Store);
  private readonly router = inject(Router);

  private static readonly FOLDER_PATH = 'Data Dictionary/ACA';
  private static readonly FILE_NAME = 'menu-permissions.json';

  /** Alfresco's built-in administrators group — always sees everything. */
  static readonly ADMIN_GROUP = 'GROUP_ALFRESCO_ADMINISTRATORS';

  /** The menus a policy may grant — the navigation entries and the settings sections. */
  static readonly MENU_ITEMS: MenuItemDef[] = [
    { key: 'insights', id: 'app.navbar.documents.insights', route: 'insights', kind: 'nav', titleKey: 'DMS.NAV.INSIGHTS' },
    { key: 'upload', id: 'app.navbar.documents.upload', route: 'upload', kind: 'nav', titleKey: 'DMS.NAV.UPLOAD' },
    { key: 'forms', id: 'app.navbar.documents.forms', route: 'forms', kind: 'nav', titleKey: 'DMS.NAV.FORMS' },
    { key: 'all', id: 'app.navbar.documents.all', route: 'all-documents', kind: 'nav', titleKey: 'DMS.NAV.ALL' },
    { key: 'recent', id: 'app.navbar.documents.recent', route: 'recent-files', kind: 'nav', titleKey: 'DMS.NAV.RECENT' },
    { key: 'search', id: 'app.navbar.documents.search', route: 'document-search', kind: 'nav', titleKey: 'DMS.NAV.SEARCH' },
    { key: 'favorites', id: 'app.navbar.documents.favorites', route: 'my-favorites', kind: 'nav', titleKey: 'DMS.NAV.FAVORITES' },
    { key: 'sharelinks', id: 'app.navbar.documents.sharelinks', route: 'share-links', kind: 'nav', titleKey: 'DMS.NAV.SHARE_LINKS' },
    { key: 'startwf', id: 'app.navbar.documents.startwf', route: 'workflows/start', kind: 'nav', titleKey: 'DMS.NAV.START_WF' },
    { key: 'work', id: 'app.navbar.documents.work', route: 'my-work', kind: 'nav', titleKey: 'DMS.NAV.MY_WORK' },
    { key: 'track', id: 'app.navbar.documents.track', route: 'workflows/track', kind: 'nav', titleKey: 'DMS.NAV.TRACK' },
    // management moved inside settings — the menu id stays so stored policies
    // still match, but the route it protects is the settings deep link now
    { key: 'manage', id: 'app.navbar.documents.manage', route: 'settings/workflows', kind: 'nav', titleKey: 'DMS.NAV.MANAGE_WF' },
    // the old approvals menu — kept so stored policies still match something
    { key: 'approvals', id: 'app.navbar.documents.approvals', route: 'my-work', kind: 'nav', titleKey: 'DMS.NAV.APPROVALS' },
    { key: 'repository', id: 'app.navbar.documents.repository', route: 'repository', kind: 'nav', titleKey: 'DMS.NAV.REPOSITORY' },
    { key: 'copymove', id: 'app.navbar.documents.copy-move', route: 'copy-move', kind: 'nav', titleKey: 'DMS.NAV.COPY_MOVE' },
    { key: 'trash', id: 'app.navbar.documents.trash', route: 'my-trash', kind: 'nav', titleKey: 'DMS.NAV.TRASH' },
    { key: 'settings', id: 'app.navbar.admin.tools', route: 'settings', kind: 'nav', titleKey: 'DMS.NAV.SETTINGS' },
    // The sections inside the settings page — they have no route of their own;
    // the settings page asks isMenuAllowed() directly for each of them.
    { key: 'set-profile', id: 'app.settings.profile', kind: 'settings', titleKey: 'DMS.UI.E2357B' },
    { key: 'set-admin', id: 'app.settings.admin', kind: 'settings', titleKey: 'DMS.UI.091FB6' },
    { key: 'set-usage', id: 'app.settings.usage', kind: 'settings', titleKey: 'DMS.UI.93BAE0' },
    { key: 'set-fields', id: 'app.settings.fields', kind: 'settings', titleKey: 'DMS.FIELDS.TITLE' },
    { key: 'set-retention', id: 'app.settings.retention', kind: 'settings', titleKey: 'DMS.UI.8F7F73' },
    { key: 'set-workflow', id: 'app.settings.workflow', kind: 'settings', titleKey: 'DMS.WORKFLOW.TITLE' },
    { key: 'set-migration', id: 'app.settings.migration', kind: 'settings', titleKey: 'DMS.MIGRATION.TITLE' },
    { key: 'set-assistant', id: 'app.settings.assistant', kind: 'settings', titleKey: 'DMS.ASSISTANT.TITLE' }
  ];

  /**
   * The system policy — shown in the list and honoured in matching, but never
   * stored, so it can be neither edited nor deleted. It exists so that the
   * Alfresco administrators group always sees every menu even when the
   * default-deny rule is in force.
   */
  static readonly BUILTIN: MenuPolicy = {
    id: 'builtin-administrators',
    name: 'DMS.MENUPERM.BUILTIN_NAME',
    users: [],
    groups: [MenuPermissionsService.ADMIN_GROUP],
    menus: MenuPermissionsService.MENU_ITEMS.map((m) => m.id)
  };

  /** The policies the administrator saved — `BUILTIN` is never among them. */
  readonly policies = signal<MenuPolicy[]>([]);

  /** The signed-in person, for policy matching. */
  private readonly me = signal<Viewer | null>(null);

  private loaded = false;
  private readonly loadedSubject = new ReplaySubject<void>(1);

  /** Emits once the stored policies have been read — the guard waits on it. */
  readonly whenLoaded: Observable<void> = this.loadedSubject.asObservable();

  /**
   * Reads the policies and the current person once per session. The guard
   * waits on `whenLoaded`, which must therefore fire only after both are in —
   * a matching person who checked before `me` resolved would be denied
   * wrongly.
   */
  load(): void {
    if (this.loaded) {
      return;
    }
    this.loaded = true;

    from(this.fetchViewer())
      .pipe(
        catchError(() => of(null)),
        switchMap((viewer) => {
          if (viewer) {
            this.me.set(viewer);
          }
          return this.read().pipe(catchError(() => of(null)));
        })
      )
      .subscribe((saved) => {
        if (saved) {
          this.policies.set(saved);
        }
        this.loadedSubject.next();
        this.refreshNavigation();
      });
  }

  /** Writes the whole policy list for everyone. Requires an administrator. */
  save(policies: MenuPolicy[]): Observable<void> {
    const clean = MenuPermissionsService.sanitize(policies);
    return this.nodeId(true).pipe(
      switchMap((nodeId) => from(this.nodes().updateNodeContent(nodeId, JSON.stringify({ policies: clean })) as Promise<any>)),
      tap(() => {
        this.policies.set(clean);
        this.refreshNavigation();
      }),
      map(() => undefined)
    );
  }

  /**
   * Whether the menu may appear for the person looking at it.
   *
   * Strictly denied by default once the feature is in use: with no saved
   * policy at all every menu shows (the deployment the app shipped with);
   * with at least one, a menu shows only when a policy that names it also
   * names the viewer — directly or through a group. Administrators bypass
   * the check entirely.
   */
  isMenuAllowed(menuId: string): boolean {
    const saved = this.policies();
    if (saved.length === 0) {
      return true;
    }
    const me = this.me();
    if (me?.isAdmin || me?.groups.has(MenuPermissionsService.ADMIN_GROUP)) {
      return true;
    }
    return [MenuPermissionsService.BUILTIN, ...saved].some((p) => p.menus.includes(menuId) && this.memberOf(p));
  }

  /**
   * Whether the route behind a menu may be opened — the guard's question.
   *
   * The most specific menu wins: `settings/workflows` answers to its own menu
   * rather than to "settings", and `repository/:folderId` or
   * `my-work/task/:id` answer the way `repository` and `my-work` do.
   */
  isRouteAllowed(routePath: string): boolean {
    const menu = MenuPermissionsService.MENU_ITEMS.filter((m) => m.route && (routePath === m.route || routePath.startsWith(`${m.route}/`))).sort(
      (a, b) => (b.route?.length ?? 0) - (a.route?.length ?? 0)
    )[0];
    return menu ? this.isMenuAllowed(menu.id) : true;
  }

  /**
   * Where to send someone whose landing menu is closed to them. A menu the
   * workflow switch has already closed is skipped — redirecting there would
   * bounce back here and loop forever.
   */
  firstAllowedRoute(): string {
    const menu = MenuPermissionsService.MENU_ITEMS.find((m) => this.isMenuAllowed(m.id) && this.featureOpen(m));
    return menu ? `/${menu.route}` : '/dashboard';
  }

  /** The workflow menus answer to the feature flag as well as to policies. */
  private featureOpen(menu: MenuItemDef): boolean {
    if (this.workflowSettings.enabled()) {
      return true;
    }
    return !['work', 'approvals', 'startwf', 'track', 'manage'].includes(menu.key);
  }

  private memberOf(policy: MenuPolicy): boolean {
    const me = this.me();
    if (!me) {
      return false;
    }
    return policy.users.includes(me.id) || policy.groups.some((g) => me.groups.has(g));
  }

  /**
   * The sidenav builds its menu from the store's navigation state; storing
   * the current url again makes it re-evaluate the `app.menu.*` rules now,
   * instead of at the next navigation.
   */
  private refreshNavigation(): void {
    this.store.dispatch(new SetCurrentUrlAction(this.router.url));
  }

  private async fetchViewer(): Promise<Viewer | null> {
    const { entry: user } = await new PeopleApi(this.api.getInstance() as any).getPerson('-me-');
    if (!user) {
      return null;
    }
    const memberships = await this.groupService.listAllGroupMembershipsForPerson('-me-', { maxItems: 250 });
    const groups = new Set<string>((memberships ?? []).map((g) => g.entry?.id).filter(Boolean));

    // capabilities is absent for an administrator on some repository versions
    return { id: user.id, isAdmin: user.capabilities ? !!user.capabilities.isAdmin : true, groups };
  }

  private read(): Observable<MenuPolicy[] | null> {
    return this.nodeId(false).pipe(
      switchMap((nodeId) => (nodeId ? from(this.nodes().getNodeContent(nodeId) as Promise<any>) : of(null))),
      switchMap((content: any) => (content ? this.text(content) : of(null))),
      map((text) => {
        if (!text) {
          return null;
        }
        try {
          return MenuPermissionsService.sanitize((JSON.parse(text) as { policies?: MenuPolicy[] }).policies ?? []);
        } catch {
          // a hand-edited or truncated document must not hide every menu
          return null;
        }
      })
    );
  }

  /** A Blob on the browser, but the API's typings promise only `any`. */
  private text(content: any): Observable<string | null> {
    if (typeof content === 'string') {
      return of(content);
    }
    if (content instanceof Blob) {
      return from(content.text());
    }
    return of(null);
  }

  /** Trims policies into a safe shape — unknown menus and dupes dropped. */
  private static sanitize(policies: MenuPolicy[]): MenuPolicy[] {
    const known = new Set(MenuPermissionsService.MENU_ITEMS.map((m) => m.id));
    return (Array.isArray(policies) ? policies : [])
      .filter((p) => p && p.id !== MenuPermissionsService.BUILTIN.id)
      .map((p) => ({
        id: String(p.id || `pol-${Date.now()}`),
        name: String(p.name ?? '').slice(0, 80),
        users: [...new Set((p.users ?? []).map(String))],
        groups: [...new Set((p.groups ?? []).map(String))],
        menus: [...new Set((p.menus ?? []).filter((m) => known.has(m)))]
      }));
  }

  /**
   * The document's node id, creating the folder and the document on the way
   * when `create` is set — which only ever happens on an administrator's save.
   */
  private nodeId(create: boolean): Observable<string | null> {
    const nodes = this.nodes();

    // listed once for every settings document — see AcaConfigFolderService
    return this.configFolder.find(MenuPermissionsService.FILE_NAME).pipe(
      catchError(() => {
        if (!create) {
          return of(null);
        }

        // the folder may or may not be there; either way the document is not
        return from(nodes.createNode('-root-', { name: 'ACA', nodeType: 'cm:folder', relativePath: 'Data Dictionary' } as any) as Promise<any>).pipe(
          catchError(() => of(null)),
          switchMap(() =>
            from(
              nodes.createNode('-root-', {
                name: MenuPermissionsService.FILE_NAME,
                nodeType: 'cm:content',
                relativePath: MenuPermissionsService.FOLDER_PATH
              } as any) as Promise<any>
            )
          ),
          map((entry: any) => entry?.entry?.id as string),
          tap(() => this.configFolder.forget())
        );
      })
    );
  }

  private nodes(): NodesApi {
    return new NodesApi(this.api.getInstance() as any);
  }
}
